/* eslint no-use-before-define: 0 */

import clone from 'clone';
import moment from 'moment';
import {bin, extent, range} from 'd3-array';
import {getUniqueId} from 'utils/id-util';
import {getLowestValueAboveZero} from 'utils/number-util';
import {numericAscending} from 'utils/sort-util';

export const getBins = (values, _numBins = 5) => {
  // Guard against invalid numBins
  const numBins = _numBins < 1 ? 1 : _numBins;
  return (
    bin()
      // Providing a custom function to thresholds to force the exact number of bins
      // specified. The thresholds function does not guarantee that passing a number
      // will yield the exact number of bins.
      // https://observablehq.com/@d3/d3-bin#cell-1412
      .thresholds((data, min, max) =>
        range(numBins).map((t) => min + (t / numBins) * (max - min)),
      )
      .domain([getLowestValueAboveZero(values), Math.max(...values)])(values)
  );
};

export const getExtentsFromBins = (bins) => {
  return bins.map((binItem) => extent(binItem));
};

export const getFilteredSeries = (groups, filterKey) => {
  const series = getSeriesFromGroups(groups)
    // Ensure that all series have a value for legendIndex:
    .map((datum, index) => {
      let legendIndex = index;
      if (datum.legendIndex || datum.legendIndex === 0) {
        legendIndex = datum.legendIndex;
      }
      return {
        ...datum,
        legendIndex,
      };
    })
    // Filter series based on passed key:
    .filter((datum) => !datum[filterKey]);

  // Sort remaining series on legendIndex:
  return numericAscending(series, 'legendIndex');
};

export const getMaxLineIndex = (children) => {
  if (children && children.length) {
    return Math.max(...children.map((datum) => datum.lineIndex)) + 1 || 0;
  }
  return 0;
};

export const getParsedGroups = (groups) => {
  return groups && groups.length
    ? groups.map((group) => {
        const cloned = clone(group);
        cloned.series.forEach((series) => {
          setSeriesDates(series);
          setSeriesId(series);
          setSeriesType(series, group);
        });
        return cloned;
      })
    : [];
};

export const getSeriesFromGroups = (groups) => {
  return (
    groups
      // Pull all series into a single array:
      .map((group) => group.series)
      // Flatten series array:
      .reduce((previous, current) => previous.concat(current))
  );
};

export const setSeriesDates = (series) => {
  // Parse date strings to dates. We need Moment,
  // otherwise the native JS Date will shift the
  // timestamp into the user's timezone, and all
  // dates will be off by a number of hours.
  series.data.forEach((datum) => {
    if (datum.hasOwnProperty('date') && typeof datum.date === 'string') {
      datum.date = moment(datum.date).toDate();
    }
  });
};

export const setSeriesId = (series) => {
  // A unique series ID is required for events.
  // The ID is used to identify which series
  // dispatched a given event.
  series.id = getUniqueId();
};

export const setSeriesType = (series, group) => {
  // If a series has no type set, attempt
  // to inherit type from the parent group.
  if (!series.type) {
    series.type = group.type;
  }
};
